"use client"

import { Button } from "@/components/ui/button"
import { getIsFraudListed, signout } from "@lib/data/customer"
import { Prompt } from "@medusajs/ui"
import Link from "next/link"
import { Shield } from "lucide-react"
import { useEffect, useState } from "react"

interface FraudModalProps {
    isOpen: boolean
    onClose?: () => void
}

type FraudStatus = {
    id: number
    customer_id: number
    transaction_id: number
    gateway_id: number
    updated_by: null | number
    status: string
    reason: string
    created_at: string
}

export function FraudModal({ isOpen, onClose }: FraudModalProps) {
    const [flag, setFlag] = useState<FraudStatus[] | null>(null);

    useEffect(() => {
        const checkFraud = async () => {
            const result = await getIsFraudListed();
            setFlag(result);
        };
        checkFraud();
    }, []);

    const handleLogout = async () => {
        await signout();
    };

    const hasFlag = Boolean(flag?.[0]);
    const isPending = flag?.[0]?.status === "pending";
    const isActive = flag?.[0]?.status === "active";
    const rejectionReason = flag?.[0]?.reason;
    const showSubmitDocuments = hasFlag && !isPending;

    return (
        <Prompt open={isOpen} onOpenChange={onClose}>
            <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
            <Prompt.Content
                className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white/95 backdrop-blur-md p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
            >
                <div className="rounded-lg bg-red-50/80 backdrop-blur-sm p-4 border border-red-200/50">
                    <div className="flex items-center justify-center space-x-2 text-red-700">
                        <Shield className="h-5 w-5" />
                        <span className="font-medium">Security Notice</span>
                    </div>
                    {isPending ? (
                        <>
                            <p className="mt-2 text-sm text-red-600 text-center">
                                Your account is under review. Please wait for verification to complete.
                            </p>
                            <p className="mt-2 text-sm text-red-600 text-center">
                                This may take 24-48 hours. Contact support if urgent.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="mt-2 text-sm text-red-600 text-center">
                                We&apos;re conducting a quick check on your account to verify recent activity.
                            </p>
                            <p className="mt-2 text-sm text-red-600 text-center">
                                {hasFlag && rejectionReason
                                    ? "Your account requires additional verification."
                                    : "Your account needs verification to continue."
                                }
                            </p>
                            {isActive && rejectionReason && (
                                <p className="mt-2 text-sm text-red-600 text-center">
                                    <strong>Reason:</strong> {rejectionReason}
                                </p>
                            )}
                        </>
                    )}
                </div>
                <div className="space-y-4 text-center">
                    <div className="space-y-2">
                        {showSubmitDocuments && (
                            <Link
                                href={`/account/id-verification/fraud`}
                            >
                                <Button
                                    className="text-black w-full text-sm bg-white backdrop-blur-sm hover:bg-button-primary px-3 hover:text-white rounded-lg mt-4"
                                >
                                    {isActive && rejectionReason ? "Submit New Documents" : "Verify Account"}
                                </Button>
                            </Link>
                        )}
                        <Button
                            className="text-black w-full text-sm bg-white backdrop-blur-sm hover:bg-button-primary px-3 hover:text-white rounded-lg"
                            onClick={handleLogout}
                        >
                            Log out
                        </Button>
                    </div>
                </div>
            </Prompt.Content>
        </Prompt>
    )
}